import { AddressSource, ProgramEventVenue, Rule } from '@citruscamps/citrus-client'
import { IANAZone } from 'luxon'
import validator from 'validator'
import * as Yup from 'yup'
import translations from '../../../../../translations/en.json'
import { getMillisecondsFromHours } from '../../../../../utils/miliseconds-conversion'
import { MaxSummaryLength } from '../components/AppointmentTypeDescription/AppointmentTypeDescriptionForm'

const LogisticsDefinition = {
  type: Yup.mixed<'camp' | 'session'>()
    .oneOf(['camp', 'session'], (translations as any)['error.default_inputs_message'])
    .required((translations as any)['error.event_type_required']),
  name: Yup.string()
    .required((translations as any)['error.name_required'])
    .max(100, (translations as any)['error.event_name_maxlength']),
  is_online_event: Yup.boolean(),
  venues: Yup.array<ProgramEventVenue>().when('is_online_event', {
    is: false,
    then: Yup.array<ProgramEventVenue>()
      .of(
        Yup.object({
          source: Yup.mixed<AddressSource>()
            .oneOf(['mapbox', 'custom'], (translations as any)['error.default_inputs_message'])
            .required((translations as any)['error.default_inputs_message']),
          place_type: Yup.string().notRequired().nullable(),
          place_name: Yup.string().notRequired().nullable(),
          latitude: Yup.string().notRequired().nullable(),
          longitude: Yup.string().notRequired().nullable(),
          address_line_1: Yup.string()
            .notRequired()
            .nullable()
            .when('source', {
              is: 'custom',
              then: Yup.string().required((translations as any)['error.address_required']),
            }),
          address_line_2: Yup.string().notRequired().nullable(),
          city: Yup.string()
            .notRequired()
            .nullable()
            .when('source', {
              is: 'custom',
              then: Yup.string().required((translations as any)['error.address_city_required']),
            }),
          region_short_code: Yup.string()
            .notRequired()
            .nullable()
            .when('source', {
              is: 'custom',
              then: Yup.string().required((translations as any)['error.address_region_required']),
            }),
          postcode: Yup.string()
            .notRequired()
            .nullable()
            .when('source', {
              is: 'custom',
              then: Yup.string().required((translations as any)['error.address_postcode_required']),
            }),
          country_short_code: Yup.string()
            .notRequired()
            .nullable()
            .when('source', {
              is: 'custom',
              then: Yup.string().required((translations as any)['error.address_country_required']),
            }),
          geocode_feature: Yup.object()
            .notRequired()
            .nullable()
            .when('source', {
              is: 'mapbox',
              then: Yup.object().required((translations as any)['error.address_required']),
            }),
        }),
      )
      .min(1, (translations as any)['error.event_venues_required']),
    otherwise: Yup.array<ProgramEventVenue>().notRequired(),
  }),
  online_event_url: Yup.string().when('is_online_event', {
    is: true,
    then: Yup.string()
      .required((translations as any)['error.event_online_event_url_required'])
      .test(
        'invalid',
        (translations as any)['error.event_online_event_url_invalid'],
        function (val) {
          return !!val && validator.isURL(val)
        },
      ),
    otherwise: Yup.string().notRequired(),
  }),
  timezone: Yup.string().test(
    'timezone',
    (translations as any)['error.event_timezone_invalid'],
    (val) => !val || (!!val && IANAZone.isValidZone(val)),
  ),
  feedback_enabled: Yup.boolean().required((translations as any)['error.default_inputs_message']),
}

export const ScheduleDefinition = {
  period_type: Yup.string(),
  max_booking_time: Yup.number()
    .notRequired()
    .when('period_type', {
      is: 'moving',
      then: Yup.number()
        .required((translations as any)['error.max_booking_time_required'])
        .test('min', (translations as any)['error.max_booking_time_min'], function (val) {
          return !!val && val > 0
        }),
    }),
  is_start_time_flexible: Yup.boolean(),
  spot_step: Yup.number().when('is_start_time_flexible', {
    is: true,
    then: Yup.number()
      .test('max', (translations as any)['error.spot_step_max'], function (val) {
        return !val || val <= getMillisecondsFromHours(3)
      })
      .required((translations as any)['error.spot_step_required']),
    otherwise: Yup.number().notRequired(),
  }),
  daily_events_limit: Yup.number()
    .nullable()
    .notRequired()
    .integer((translations as any)['error.daily_limit_invalid'])
    .min(1, (translations as any)['error.daily_events_min']),
  min_booking_time: Yup.number()
    .required()
    .test('min', (translations as any)['error.min_booking_time_min'], function (val) {
      return !!val && val > 0
    }),
  min_notice_unit: Yup.string().required(),
  before_buffer_time: Yup.number()
    .nullable()
    .notRequired()
    .test('max', (translations as any)['error.buffer_time_max'], function (val) {
      return !val || val <= getMillisecondsFromHours(3)
    }),
  after_buffer_time: Yup.number()
    .nullable()
    .notRequired()
    .test('max', (translations as any)['error.buffer_time_max'], function (val) {
      return !val || val <= getMillisecondsFromHours(3)
    }),
  starts_at: Yup.date()
    .nullable()
    .test('required', (translations as any)['error.event_starts_at_required'], function (val) {
      return this.parent.period_type !== 'fixed' || !!val
    })
    .test('max', (translations as any)['error.starts_at_date_after_ends_at_date'], function (val) {
      return this.parent.period_type !== 'fixed' || (!!val && val < this.parent.ends_at)
    }),
  ends_at: Yup.date()
    .nullable()
    .test('required', (translations as any)['error.event_ends_at_required'], function (val) {
      return this.parent.period_type !== 'fixed' || !!val
    })
    .test('max', (translations as any)['error.ends_at_date_after_starts_at_date'], function (val) {
      return this.parent.period_type !== 'fixed' || (!!val && val > this.parent.starts_at)
    }),
}

export const ScheduleRulesDefinition = {
  ...ScheduleDefinition,
  rules: Yup.array<Rule>().test(
    'min',
    (translations as any)['error.rules_min_required'],
    function (val) {
      return !!val?.some((a) => (a.intervals || []).length > 0)
    },
  ),
}

const DescriptionDefinition = {
  summary: Yup.string()
    .required((translations as any)['error.event_summary_required'])
    .max(MaxSummaryLength, (translations as any)['error.event_summary_maxlength']),
  description: Yup.string(),
  picture: Yup.object()
    .test('required', (translations as any)['error.event_picture_invalid'], function (val) {
      return !val || !!val.id
    })
    .nullable(),
  members_only: Yup.boolean(),
}

const OrderOptionsDefinition = {
  pricing: Yup.object().shape({
    amount_of_options: Yup.number()
      .required((translations as any)['error.appointment_option_required'])
      .min(1, (translations as any)['error.appointment_option_required']),
  }),
}

const AppointmentTypeSchemaDefinition = {
  ...LogisticsDefinition,
  ...ScheduleRulesDefinition,
  ...DescriptionDefinition,
  ...OrderOptionsDefinition,
}

type AppointmentPartialType = 'logistics' | 'schedule_rules' | 'description' | 'options'

export class AppointmentTypeSchema {
  static get() {
    return Yup.object().shape({
      ...AppointmentTypeSchemaDefinition,
      id: Yup.string().required((translations as any)['error.default_inputs_message']),
      object: Yup.string(),
      profiles: Yup.array(),
    })
  }

  static getPartial(type: AppointmentPartialType) {
    switch (type) {
      case 'logistics':
        return Yup.object().shape(LogisticsDefinition)
      case 'schedule_rules':
        return Yup.object().shape(ScheduleRulesDefinition)
      case 'description':
        return Yup.object().shape(DescriptionDefinition)
      case 'options':
        return Yup.object().shape(OrderOptionsDefinition)
      default:
        return AppointmentTypeSchema.get()
    }
  }
}
